import type React from "react";
import { useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { Button, Divider } from "semantic-ui-react";

import { AccessibleModal } from "./Accessibility/AccessibleModal";

type ExternalLinkModalProps = {
  href: string;
  color?: string;
  onClose?: () => void;
  onFollow?: () => void;
} & (
  | { open: boolean; children?: never }
  | { children: React.ReactNode; open?: never }
);

export const ExternalLinkModal = ({
  children,
  color,
  open,
  href,
  onClose,
  onFollow,
}: ExternalLinkModalProps) => {
  const [controlledOpen, setControlledOpen] = useState(false);
  const { t } = useTranslation();

  const sureHref = useMemo(() => {
    return href?.startsWith("http") ? href : `https://${href}`;
  }, [href]);

  return (
    <>
      <AccessibleModal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        small
        open={open || controlledOpen}
        onClose={() => {
          onClose?.();
          setControlledOpen(false);
        }}
      >
        <AccessibleModal.Header>Warning</AccessibleModal.Header>
        <AccessibleModal.Content>
          <AccessibleModal.Description>
            You are about to leave the safety of the SimplyDo platform to follow
            an external link. Please make sure that you can trust the linked
            resource before proceeding.
            <br />
            <br />
            Please make sure to never enter your login information anywhere but
            on the SimplyDo platform.
          </AccessibleModal.Description>
          <Divider />
          <AccessibleModal.Description>
            <b>{sureHref}</b>
          </AccessibleModal.Description>
        </AccessibleModal.Content>
        <AccessibleModal.Actions>
          <Button
            content="Open link in a new tab"
            secondary
            onClick={() => {
              window.open(sureHref, "_blank");
              setControlledOpen(false);
              onFollow?.();
            }}
          />
          <Button
            content={t("generic.cancel")}
            onClick={() => {
              setControlledOpen(false);
              onClose?.();
            }}
          />
        </AccessibleModal.Actions>
      </AccessibleModal>
      {children ?
        <a
          onClick={(e) => {
            e.preventDefault();
            setControlledOpen(true);
          }}
          href={sureHref}
          style={{ color }}
        >
          {children}
        </a>
      : null}
    </>
  );
};
